import React, { useContext, useState } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import CssBaseline from '@mui/material/CssBaseline';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import SettingsIcon from '@mui/icons-material/Settings';
import { Box, Avatar, Chip } from '@mui/material';
import { UserContext } from '../contexts/userContext';
import { useHistory } from 'react-router';
import TicketContent from './tickets/TicketContent';
import ChangePasswordDialog from './components/ChangePasswordDialog';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    minHeight: '100vh',
    background: theme.palette.background.default,
  },
  appBar: {
    zIndex: 1001,
    background:
      theme.palette.mode === 'dark'
        ? `linear-gradient(120deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
        : `linear-gradient(120deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
    boxShadow: 'none',
    borderBottom: `1px solid ${
      theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.2)'
    }`,
  },
  toolbar: {
    paddingRight: 24,
    paddingLeft: 24,
    minHeight: '56px !important',
    height: 56,
  },
  title: {
    flexGrow: 1,
    fontWeight: 700,
    fontSize: '1.2rem',
    color: '#FFFFFF',
    letterSpacing: '-0.4px',
  },
  content: {
    flexGrow: 1,
    paddingTop: 56,
    overflow: 'auto',
    background: theme.palette.mode === 'dark' ? theme.palette.background.default : '#F8F9FA',
    minHeight: '100vh',
  },
}));

export default function TicketDashboard() {
  const classes = useStyles();
  const {
    setLoginStatus,
    setUserName,
    setRole,
    setUserEmail,
    setReferTo,
    setFullAccess,
  } = useContext(UserContext);
  const user = JSON.parse(localStorage.getItem('user'));
  const history = useHistory();
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);

  const logout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('MagexApp.cart');
    localStorage.removeItem('machineName');
    localStorage.removeItem('reRender');
    localStorage.removeItem('limits');
    setLoginStatus(false);
    setUserName('');
    setRole('');
    setUserEmail('');
    setReferTo('');
    setFullAccess(false);
    history.push('/signIn');
  };

  return (
    <div className={classes.root}>
      <CssBaseline />
      <AppBar position="fixed" className={classes.appBar}>
        <Toolbar className={classes.toolbar}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 2 }}>
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                fontWeight: 700,
                fontSize: '0.75rem',
              }}
            >
              {user?.firstname?.charAt(0)}
              {user?.lastname?.charAt(0)}
            </Avatar>
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  color: 'white',
                  lineHeight: 1.2,
                }}
              >
                {`${user?.firstname} ${user?.lastname}`}
              </Typography>
              <Chip
                label={user?.role === 'SubTicketUser' ? 'Sub Ticket User' : 'Ticket User'}
                size="small"
                sx={{
                  height: 16,
                  fontSize: '0.6rem',
                  fontWeight: 600,
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                }}
              />
            </Box>
          </Box>
          <Typography component="h1" variant="h6" noWrap className={classes.title}>
            Ticket Dashboard
          </Typography>
          <IconButton
            aria-label="Settings"
            onClick={() => setChangePasswordOpen(true)}
            size="large"
            sx={{
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            <SettingsIcon />
          </IconButton>
          <IconButton
            aria-label="Exit"
            onClick={logout}
            size="large"
            sx={{
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            <ExitToAppIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <ChangePasswordDialog
        open={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
        userEmail={user?.email}
        userId={user?._id}
      />
      <main className={classes.content}>
        <TicketContent />
      </main>
    </div>
  );
}
